import { api, dashboardApi, presentationApi } from '@ardoq/api';
import {
  APIReportFilters,
  ArdoqId,
  WidgetDatasource,
  WidgetGlobalFilterBindings,
} from '@ardoq/api-types';
import { isArdoqError } from '@ardoq/common-helpers';
import {
  DashboardFilter,
  dashboardFilterOperations,
  FlatDashboardFilterSuggestions,
  HierarchicalDashboardFilterSuggestions,
  UnlinkedDashboardFilter,
  HierarchicalDashboardFilterSuggestion,
  filterAndLimitHierarchy,
  toHierarchicalDashboardFilterSuggestions,
} from '@ardoq/dashboard';
import {
  action$,
  actionCreator,
  extractPayload,
  ofType,
  reducedStream,
  reducer,
  streamReducer,
} from '@ardoq/rxbeach';
import { omit } from 'lodash';
import { from, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

type DashboardFiltersSuggestionsState =
  | FlatDashboardFilterSuggestions
  | (HierarchicalDashboardFilterSuggestions & {
      cachedSuggestions: HierarchicalDashboardFilterSuggestion[];
    });
type DashboardFiltersSuggestionsStateById = Record<
  ArdoqId, // dashboard filter parameter id
  DashboardFiltersSuggestionsState
>;

/**
 * Suggestions for a dashboard shown as a presentation slide go through the
 * presentation-scoped endpoint, which resolves permissions from the
 * presentation rather than the viewer.
 */
export type PresentationSuggestionsContext = {
  presentationId: ArdoqId;
  slideId: ArdoqId;
};

export type RequestDashboardFilterSuggestionsPayload =
  | {
      isInDashboardBuilder: false;
      presentationContext: PresentationSuggestionsContext | null;
      dashboardId: ArdoqId;
      parameterId: ArdoqId;
      query: string;
      dashboardFilters: Array<UnlinkedDashboardFilter | DashboardFilter>;
    }
  | {
      isInDashboardBuilder: true;
      dashboardId?: ArdoqId;
      parameterId: ArdoqId;
      query: string;
      dashboardFilters: Array<UnlinkedDashboardFilter | DashboardFilter>;
      widgetsSupportDashboardFilters: Array<{
        _id: ArdoqId;
        datasource: WidgetDatasource;
        globalFilterBindings?: WidgetGlobalFilterBindings;
        filters?: APIReportFilters;
      }>;
    };
export const requestDashboardFilterSuggestions =
  actionCreator<RequestDashboardFilterSuggestionsPayload>(
    '[dashboardFiltersSuggestions] REQUEST_DASHBOARD_FILTER_SUGGESTIONS'
  );

export type RequestDashboardFilterHierarchicalSuggestionsPayload =
  RequestDashboardFilterSuggestionsPayload;

export const requestDashboardFilterHierarchicalSuggestions =
  actionCreator<RequestDashboardFilterHierarchicalSuggestionsPayload>(
    '[dashboardFiltersSuggestions] REQUEST_DASHBOARD_FILTER_HIERARCHICAL_SUGGESTIONS'
  );

type FilterCachedHierarchicalSuggestionsPayload = {
  parameterId: ArdoqId;
  query: string;
};

export const filterCachedHierarchicalSuggestions =
  actionCreator<FilterCachedHierarchicalSuggestionsPayload>(
    '[dashboardFiltersSuggestions] GET_DASHBOARD_FILTER_HIERARCHICAL_SUGGESTIONS_FROM_CACHE'
  );

const handleRequestDashboardFilterSuggestions = (
  state: DashboardFiltersSuggestionsStateById,
  {
    parameterId,
    query,
    dashboardFilters,
  }: RequestDashboardFilterSuggestionsPayload
): DashboardFiltersSuggestionsStateById => ({
  ...state,
  [parameterId]: {
    kind: 'flat',
    parameterId,
    query,
    suggestions: [],
    searchedDashboardFilterValues: omit(
      dashboardFilterOperations.toFilterExpressionsById(dashboardFilters),
      parameterId
    ),
    isLoading: true,
  },
});

const handleRequestDashboardFilterHierarchicalSuggestions = (
  state: DashboardFiltersSuggestionsStateById,
  {
    parameterId,
    query,
    dashboardFilters,
  }: RequestDashboardFilterHierarchicalSuggestionsPayload
): DashboardFiltersSuggestionsStateById => ({
  ...state,
  [parameterId]: {
    kind: 'hierarchical',
    parameterId,
    query,
    suggestions: [],
    cachedSuggestions: [],
    searchedDashboardFilterValues: omit(
      dashboardFilterOperations.toFilterExpressionsById(dashboardFilters),
      parameterId
    ),
    isLoading: true,
  },
});

const handleUpdateDashboardFilterSuggestions = (
  state: DashboardFiltersSuggestionsStateById,
  update: DashboardFiltersSuggestionsState
): DashboardFiltersSuggestionsStateById => ({
  ...state,
  [update.parameterId]: {
    ...update,
    isLoading: false,
  },
});

const requestedDashboardFilterSuggestions$: Observable<DashboardFiltersSuggestionsState> =
  action$.pipe(
    ofType(requestDashboardFilterSuggestions),
    extractPayload(),
    switchMap(props => {
      const searchedDashboardFilterValues = omit(
        dashboardFilterOperations.toFilterExpressionsById(
          props.dashboardFilters
        ),
        props.parameterId
      );
      return from(
        props.isInDashboardBuilder
          ? dashboardApi.getDashboardFilterSuggestionsWithUnsavedChanges({
              dashboardId: props.dashboardId,
              parameterId: props.parameterId,
              dashboardFilterParameters: props.dashboardFilters.map(
                dashboardFilterOperations.toParameter
              ),
              dashboardFilterValues: searchedDashboardFilterValues,
              widgetsSupportDashboardFilters:
                props.widgetsSupportDashboardFilters,
              query: props.query,
            })
          : props.presentationContext
            ? presentationApi.fetchDashboardSlideFilterSuggestions({
                ...props.presentationContext,
                dashboardId: props.dashboardId,
                parameterId: props.parameterId,
                globalFilterValues: searchedDashboardFilterValues,
                query: props.query,
              })
            : dashboardApi.getDashboardFilterSuggestions({
                dashboardId: props.dashboardId!,
                parameterId: props.parameterId,
                dashboardFilterValues: searchedDashboardFilterValues,
                query: props.query,
              })
      ).pipe(
        tap(r => isArdoqError(r) && api.logErrorIfNeeded(r)),
        map((r): FlatDashboardFilterSuggestions => ({
          kind: 'flat',
          parameterId: props.parameterId,
          query: props.query,
          suggestions: isArdoqError(r) ? [] : r.results,
          searchedDashboardFilterValues,
          widgetFilteredReports: isArdoqError(r)
            ? undefined
            : r.widgetFilteredReports,
        }))
      );
    })
  );

const handleFilterCachedHierarchicalSuggestions = (
  state: DashboardFiltersSuggestionsStateById,
  { parameterId, query }: FilterCachedHierarchicalSuggestionsPayload
): DashboardFiltersSuggestionsStateById => {
  const existing = state[parameterId];
  if (existing?.kind !== 'hierarchical') return state;
  return {
    ...state,
    [parameterId]: {
      ...existing,
      query,
      suggestions: filterAndLimitHierarchy(existing.cachedSuggestions, query),
      isLoading: false,
    },
  };
};

const requestedDashboardFilterHierarchicalSuggestions$: Observable<DashboardFiltersSuggestionsState> =
  action$.pipe(
    ofType(requestDashboardFilterHierarchicalSuggestions),
    extractPayload(),
    switchMap(props => {
      const searchedDashboardFilterValues = omit(
        dashboardFilterOperations.toFilterExpressionsById(
          props.dashboardFilters
        ),
        props.parameterId
      );
      return from(
        props.isInDashboardBuilder
          ? dashboardApi.getDashboardFilterSuggestionsWithUnsavedChanges({
              dashboardId: props.dashboardId,
              parameterId: props.parameterId,
              dashboardFilterParameters: props.dashboardFilters.map(
                dashboardFilterOperations.toParameter
              ),
              dashboardFilterValues: searchedDashboardFilterValues,
              widgetsSupportDashboardFilters:
                props.widgetsSupportDashboardFilters,
              isWithHierarchy: true,
            })
          : props.presentationContext
            ? presentationApi.fetchDashboardSlideFilterSuggestions({
                ...props.presentationContext,
                dashboardId: props.dashboardId,
                parameterId: props.parameterId,
                globalFilterValues: searchedDashboardFilterValues,
                isWithHierarchy: true,
              })
            : dashboardApi.getDashboardFilterSuggestions({
                dashboardId: props.dashboardId!,
                parameterId: props.parameterId,
                dashboardFilterValues: searchedDashboardFilterValues,
                isWithHierarchy: true,
              })
      ).pipe(
        tap(r => isArdoqError(r) && api.logErrorIfNeeded(r)),
        map(
          (
            r
          ): HierarchicalDashboardFilterSuggestions & {
            cachedSuggestions: HierarchicalDashboardFilterSuggestion[];
          } => {
            const cachedSuggestions = toHierarchicalDashboardFilterSuggestions(
              isArdoqError(r) ? [] : r.results
            );
            return {
              kind: 'hierarchical',
              parameterId: props.parameterId,
              query: props.query,
              suggestions: filterAndLimitHierarchy(
                cachedSuggestions,
                props.query ?? ''
              ),
              cachedSuggestions,
              searchedDashboardFilterValues,
              widgetFilteredReports: isArdoqError(r)
                ? undefined
                : r.widgetFilteredReports,
            };
          }
        )
      );
    })
  );

const defaultState: DashboardFiltersSuggestionsStateById = {};

export const dashboardFiltersSuggestions$ =
  reducedStream<DashboardFiltersSuggestionsStateById>(
    'dashboardFiltersSuggestions$',
    defaultState,
    [
      reducer(
        requestDashboardFilterSuggestions,
        handleRequestDashboardFilterSuggestions
      ),
      reducer(
        requestDashboardFilterHierarchicalSuggestions,
        handleRequestDashboardFilterHierarchicalSuggestions
      ),
      reducer(
        filterCachedHierarchicalSuggestions,
        handleFilterCachedHierarchicalSuggestions
      ),
      streamReducer(
        requestedDashboardFilterSuggestions$,
        handleUpdateDashboardFilterSuggestions
      ),
      streamReducer(
        requestedDashboardFilterHierarchicalSuggestions$,
        handleUpdateDashboardFilterSuggestions
      ),
    ]
  );
